import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { COLLECTION_CATEGORIES, COPY_COLLECTION_CATEGORIES, COLLECTION_LABELS } from '../constants';
import type { MigrationMode, PreviewResponse } from '../types';

interface MigrationPreviewProps {
  previewData: PreviewResponse;
  archiveSource: boolean;
  onSetArchive: (value: boolean) => void;
  mode?: MigrationMode;
}

export function MigrationPreview({
  previewData,
  archiveSource,
  onSetArchive,
  mode = 'move',
}: MigrationPreviewProps) {
  const categories = mode === 'copy' ? COPY_COLLECTION_CATEGORIES : COLLECTION_CATEGORIES;
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(Object.keys(categories)),
  );

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const getCategoryTotal = (collections: readonly string[]) => {
    return collections.reduce((sum, col) => {
      return sum + (previewData.collections[col]?.count ?? 0);
    }, 0);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Migration Preview</CardTitle>
          <Badge variant="outline" className="text-sm">
            {previewData.totalDocuments.toLocaleString()} total documents
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          From <span className="font-medium">{previewData.source.name}</span> to{' '}
          <span className="font-medium">{previewData.target.name}</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warnings */}
        {previewData.warnings.length > 0 && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Warnings</span>
            </div>
            <ul className="list-disc pl-6 text-sm text-yellow-700 dark:text-yellow-300">
              {previewData.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Copy mode info banner */}
        {mode === 'copy' && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Copy duplicates structural assets and configuration. Physical SIM cards, financial data, historical alerts, and activity logs are not included.
            </p>
          </div>
        )}

        {/* Category groups */}
        {Object.entries(categories).map(([categoryKey, category]) => {
          const total = getCategoryTotal(category.collections);
          const isExpanded = expandedCategories.has(categoryKey);
          const hasData = total > 0;

          return (
            <div key={categoryKey} className="rounded-md border">
              <button
                type="button"
                onClick={() => toggleCategory(categoryKey)}
                aria-expanded={isExpanded}
                aria-controls={`category-${categoryKey}`}
                className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span className="font-medium">{category.label}</span>
                  <span className="text-xs text-muted-foreground">{category.description}</span>
                </div>
                <Badge variant={hasData ? 'default' : 'secondary'}>
                  {total.toLocaleString()}
                </Badge>
              </button>

              {isExpanded && (
                <Table id={`category-${categoryKey}`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collection</TableHead>
                      <TableHead className="w-[100px] text-right">Count</TableHead>
                      <TableHead>Sample Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {category.collections.map((col: string) => {
                      const data = previewData.collections[col];
                      if (!data) return null;

                      return (
                        <TableRow key={col}>
                          <TableCell className="font-medium">
                            {COLLECTION_LABELS[col] || col}
                          </TableCell>
                          <TableCell className="text-right">
                            {data.count.toLocaleString()}{data.hasMore ? '+' : ''}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {data.sample.map((item, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {item}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          );
        })}

        {/* Archive option (move mode only) */}
        {mode !== 'copy' && (
          <div className="flex items-center space-x-2 pt-2 border-t">
            <Checkbox
              id="archive-source"
              checked={archiveSource}
              onCheckedChange={(checked) => onSetArchive(checked === true)}
            />
            <label htmlFor="archive-source" className="text-sm cursor-pointer">
              Archive source account after migration
            </label>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
